export const DEFAULT_CHAT_LIST_FILTERS = Object.freeze({
  viewActive: true,
  viewArchived: true,
  viewCompleted: true,
  viewInvalid: false,
  group: "",
  insuranceGrid: false,
  sort: "visitTime",
  reverse: false,
});

const BOOLEAN_FILTER_KEYS = ["viewActive", "viewArchived", "viewCompleted", "viewInvalid"];
const SORT_KEYS = ["visitTime", "createdOn", "lastUpdate", "alphabetical"];

export function normalizeChatListFilters(saved, { isAdminUser } = {}) {
  const source = saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  const normalized = { ...DEFAULT_CHAT_LIST_FILTERS };

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (typeof source[key] === "boolean") normalized[key] = source[key];
  });

  if (typeof source.group === "string") normalized.group = source.group;
  if (SORT_KEYS.includes(source.sort)) normalized.sort = source.sort;
  if (typeof source.reverse === "boolean") normalized.reverse = source.reverse;
  if (isAdminUser) normalized.group = "";

  return normalized;
}

export const clearChatListGroup = (filters) => ({ ...filters, group: "" });

export const buildChatListScopeKey = (group, currentUserId) => `${currentUserId || ""}::${group || ""}`;

export const buildChatDataIdentityKey = (currentUserId, currentOrganizationName) =>
  JSON.stringify([currentOrganizationName || "", currentUserId || ""]);

export const shouldApplyChatDataResponse = (requestIdentity, currentIdentity) => requestIdentity === currentIdentity;

export const shouldResetChatListIdentity = (previousUserId, nextUserId) => previousUserId !== nextUserId;

export const buildChatListRequestFilters = (filters, searchTerm, currentUserId) => ({
  ...filters,
  group: filters?.group === "[currentUser]" ? currentUserId || "[currentUser]" : filters?.group,
  name: searchTerm,
});

export function getLastServerScopedChat(chats, serverScopedChatIds) {
  for (let index = chats.length - 1; index >= 0; index -= 1) {
    if (serverScopedChatIds.has(chats[index].id)) return chats[index];
  }
  return undefined;
}

// --- Encounter date resolution ------------------------------------------------
//
// The encounter list's default sort is "visitTime" (see DEFAULT_CHAT_LIST_FILTERS),
// but `local:visitTime` is only written by ModalCreateFollowUp, ModalImportEvent and
// a manual edit in ModalEditPatient. The two paths that create most encounters --
// ModalNewChat and AI Quick Start -- never set it, so for a large share of records
// the default sort key is simply absent and `createdOn` is the visit time.
//
// That fallback already existed inline (`visitTime || createdOn`) but did not
// survive parsing: `createdOn` is an epoch integer, and if it arrives from
// web/organization-chats as a STRING, `new Date("1786993178008")` is Invalid Date.
// The comparator then coerced it to `new Date(0)`, sorting the encounter to 1 Jan
// 1970 -- the very bottom of a descending list -- while the day header, which
// parsed the same value with moment() rather than Date(), still rendered a
// plausible date. The encounter was in the list and unfindable. (Reported
// 2026-08-18: a signed follow-up with no visitTime, correct location and
// assignment, that its author could not find.)
//
// So: one resolver, used by the comparator AND by the day headers, so the two can
// never disagree about which day an encounter belongs to again.

/**
 * Milliseconds for a raw encounter date value, or null when it cannot be parsed.
 * Accepts an epoch number, an epoch-as-string, and any Date-parseable string.
 */
export const parseEncounterDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  // Epoch millis, as a number or as a digit-string. `new Date(<digit-string>)` is
  // Invalid Date, so the numeric form has to be recognised before falling through
  // to string parsing -- this is the coercion whose absence caused the bug above.
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value.trim());

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The encounter's visit time: `local:visitTime` when set, otherwise `createdOn`.
 * Returns null only when neither value can be parsed.
 */
export const getEncounterVisitMs = (chat) =>
  parseEncounterDate(chat?.contextVariables?.["local:visitTime"]) ?? parseEncounterDate(chat?.createdOn);

/**
 * The milliseconds the list sorts and groups by, for the given sort key.
 * Null means "no usable date" -- callers must not silently treat that as epoch 0,
 * which is what buried the encounter at the bottom of the list.
 */
export const getEncounterSortMs = (chat, sort) => {
  if (sort === "createdOn") return parseEncounterDate(chat?.createdOn);
  if (sort === "lastUpdate") return parseEncounterDate(chat?.lastUpdate);
  return getEncounterVisitMs(chat);
};
